// frontend/src/components/common/AggregationMenu.tsx
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { AggFn } from '@/types'
import { AGG_SYMBOL } from '@/utils/columnAggregations'

const MENU_W = 190
const DEFAULT_FNS: AggFn[] = ['sum', 'avg', 'min', 'max', 'count', 'countDistinct']

interface Props {
  dark: boolean
  /** Nombre de la columna, como título del popover. */
  colLabel: string
  /** Función activa, o `null` si la columna no tiene total. */
  active: AggFn | null
  onChange: (fn: AggFn | null) => void
  /**
   * Funciones a ofrecer. Por defecto todas. Una columna de texto solo admite
   * recuentos, así que puede pasar `['count', 'countDistinct']`.
   */
  fns?: AggFn[]
}

/**
 * Botón Σ que se inyecta en la cabecera de una columna numérica, más su
 * popover de funciones de agregación.
 *
 * El botón permanece invisible hasta que el ratón entra en el `<th>`, que debe
 * llevar la clase `group`; si la columna ya tiene una función activa se ve
 * siempre, resaltado.
 *
 * Reutiliza el patrón portal + overlay de ColumnsMenu: los `<th>` viven dentro
 * de contenedores con `overflow-x-auto`, así que un popover en flujo normal
 * quedaría recortado.
 */
export function AggregationMenu({ dark, colLabel, active, onChange, fns = DEFAULT_FNS }: Props) {
  const { t } = useTranslation()
  const btnRef = useRef<HTMLButtonElement>(null)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const open = rect !== null

  useEffect(() => {
    if (!open) return
    const close = () => setRect(null)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const card = dark
    ? 'bg-slate-900 border-white/10 text-slate-200'
    : 'bg-white border-slate-200 text-slate-700'
  const rowCls = `flex items-center gap-2 text-[12px] cursor-pointer px-2 py-1.5 rounded-lg ${
    dark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
  }`

  const btnCls = active
    ? 'text-sky-500'
    : `opacity-0 group-hover:opacity-100 focus-visible:opacity-100 ${
        dark ? 'text-slate-500 hover:text-slate-200' : 'text-slate-400 hover:text-slate-700'
      }`

  const left = rect ? Math.max(8, Math.min(rect.left, window.innerWidth - MENU_W - 8)) : 0
  const top = rect ? rect.bottom + 4 : 0

  function pick(fn: AggFn | null) {
    onChange(fn)
    setRect(null)
  }

  return (
    <>
      <button
        ref={btnRef}
        // El click no debe llegar al <th>, que es quien ordena la tabla.
        onClick={e => {
          e.stopPropagation()
          setRect(open ? null : (btnRef.current?.getBoundingClientRect() ?? null))
        }}
        className={`ml-0.5 shrink-0 text-[12px] font-bold leading-none cursor-pointer transition-opacity ${btnCls}`}
        title={t('common.agg.title')}
        aria-label={t('common.agg.title')}
        aria-expanded={open}
      >
        {active ? AGG_SYMBOL[active] : 'Σ'}
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setRect(null)} aria-hidden="true" />
          <div
            className={`fixed z-[61] rounded-xl border shadow-2xl p-2 ${card}`}
            style={{ top, left, width: MENU_W }}
            onClick={e => e.stopPropagation()}
          >
            <div className={`px-2 pt-1 pb-2 text-[10px] font-bold uppercase tracking-wider truncate ${
              dark ? 'text-slate-500' : 'text-slate-400'
            }`}>
              {colLabel}
            </div>
            <div className="flex flex-col gap-0.5">
              {fns.map(fn => (
                <label key={fn} className={rowCls}>
                  <input
                    type="radio"
                    checked={active === fn}
                    onChange={() => pick(fn)}
                    className="cursor-pointer accent-sky-500"
                  />
                  <span className={`w-3 text-center font-bold ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                    {AGG_SYMBOL[fn]}
                  </span>
                  <span>{t(`common.agg.${fn}`)}</span>
                </label>
              ))}
              <label className={`${rowCls} border-t mt-0.5 pt-2 ${dark ? 'border-white/10' : 'border-slate-100'}`}>
                <input
                  type="radio"
                  checked={active === null}
                  onChange={() => pick(null)}
                  className="cursor-pointer accent-sky-500"
                />
                <span className="w-3" />
                <span className={dark ? 'text-slate-400' : 'text-slate-500'}>{t('common.agg.none')}</span>
              </label>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
